import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Calendar, Sparkles, Mail } from "lucide-react";
import SectionTitle from "../components/SectionTitle";
import FloatingPetals from "../components/FloatingPetals";
import useSeo from "../hooks/useSeo";
import { business } from "../data/content";

export default function Book() {
  useSeo({
    title: `Book Now | Soft Petal Electrolysis, ${business.city}, ${business.state}`,
    description: `Book your electrolysis appointment at Soft Petal Electrolysis in ${business.city}, ${business.state}. Schedule a complimentary consultation or a treatment session in minutes.`,
    path: "/book",
  });

  return (
    <div>
      <section className="watercolor-blush py-16 lg:py-14 relative overflow-hidden">
        <FloatingPetals count={7} />
        <div className="max-w-4xl mx-auto px-6 text-center relative">
          <SectionTitle
            eyebrow="Book"
            title="Your softest chapter"
            scriptWord="starts here."
            subtitle="Welcome to Soft Petal. We can&rsquo;t wait to meet you."
          />
        </div>
      </section>

      <section className="py-12 lg:py-16">
        <div className="max-w-5xl mx-auto px-6 sm:px-10 grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-5 space-y-5">
            <InfoCard icon={Calendar} title="What to expect">
              A friendly consultation to review your goals and design a plan
              together. No pressure, just warmth and clarity.
            </InfoCard>
            <InfoCard icon={Sparkles} title="A gentle reminder">
              Please arrive with clean, product-free skin and avoid tweezing,
              waxing, or threading before your visit. Shaving is fine.
            </InfoCard>
            <InfoCard icon={Mail} title="Cancellation policy">
              We kindly ask for at least 24 hours&rsquo; notice for changes so we
              can offer your time to another Soft Petal Pal.
            </InfoCard>
          </div>

          <div className="lg:col-span-7">
            <div
              className="rounded-3xl bg-warm-white border border-pale-rose/50 shadow-soft overflow-hidden h-full flex flex-col relative"
              data-testid="booking-widget-placeholder"
            >
              <div className="flex-1 watercolor-blush flex items-center justify-center text-center p-10 min-h-[440px] relative">
                <FloatingPetals count={5} />
                <div className="relative z-10 max-w-md">
                  <div className="w-16 h-16 mx-auto rounded-full bg-warm-white flex items-center justify-center shadow-soft float-gentle">
                    <Calendar className="text-petal" size={26} />
                  </div>
                  <h3 className="font-serif-display text-3xl sm:text-4xl mt-6 leading-tight">
                    Book securely on{" "}
                    <span className="font-script text-petal text-[1.15em]">Square</span>
                  </h3>
                  <p className="mt-4 font-body text-charcoal-soft leading-relaxed">
                    See real-time availability, choose your session length, and confirm your
                    appointment in a few clicks.
                  </p>
                  <a
                    href={business.bookingUrl}
                    target="_blank"
                    rel="noreferrer noopener"
                    className="btn-petal mt-8 inline-flex soft-glow"
                    data-testid="external-booking-link"
                  >
                    Continue to Booking
                    <ArrowRight size={14} />
                  </a>
                  <p className="mt-4 text-xs font-body text-charcoal-soft/80">
                    Opens Square Appointments in a new tab.
                  </p>
                </div>
              </div>
              <div className="p-6 border-t border-pale-rose/50 flex flex-col sm:flex-row items-center justify-between gap-4">
                <p className="font-body text-sm text-charcoal-soft">
                  Need help booking? We&rsquo;re happy to assist.
                </p>
                <Link to="/contact" className="btn-ghost-petal" data-testid="book-help-btn">
                  Contact Us
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

function InfoCard({ icon: Icon, title, children }) {
  return (
    <div className="rounded-3xl bg-warm-white border border-pale-rose/50 shadow-soft p-6 sm:p-7">
      <div className="w-10 h-10 rounded-full bg-blush flex items-center justify-center">
        <Icon size={18} className="text-petal" />
      </div>
      <h3 className="mt-4 font-serif-display text-xl leading-tight">{title}</h3>
      <p className="mt-2 font-body text-sm text-charcoal-soft leading-relaxed">
        {children}
      </p>
    </div>
  );
}
